import {
    Box,
    CircularProgress,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import React, { useEffect, useState } from 'react';
import KycRevealReasonModal from './KycRevealReasonModal';
import { RevealAction, revealKeyOf, useKycReveal } from './useKycReveal';

const useStyles = makeStyles(() => ({
    row: { display: 'flex', alignItems: 'center', gap: 6 },
    link: {
        fontSize: 12,
        fontWeight: 600,
        color: '#2563EB',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
    },
    countdown: { fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' },
    unavailable: { fontSize: 12, color: '#B45309', fontStyle: 'italic' },
    disabled: {
        fontSize: 12,
        fontWeight: 600,
        color: '#9CA3AF',
        cursor: 'not-allowed',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
    },
    unmaskedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        color: '#B45309',
        background: '#FEF3C7',
        border: '1px solid #FDE68A',
        borderRadius: 4,
        padding: '2px 6px',
        whiteSpace: 'nowrap',
    },
}));

interface BaseProps {
    documentId?: number | string | null;
    documentType?: string | null;
    // Server-computed. False => the control renders disabled with a reason, so an operator can
    // tell "you may not do this" apart from "this feature does not exist here".
    revealable?: boolean | null;
    action: RevealAction;
    /**
     * Sides this control reveals. A two-sided document passes ['FRONT','BACK'] so one click
     * opens both; omit for a single-image document.
     */
    documentKeys?: Array<string | null>;
    subject: string;
}

/**
 * Seconds left on the grant, so the operator sees the value is about to collapse rather than
 * having it vanish mid-read.
 */
const useCountdown = (expiresAt?: number) => {
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!expiresAt) return undefined;
        const tick = () =>
            setLeft(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [expiresAt]);
    return left;
};

// Callers spell it AADHAR (backend doc type), AADHAAR (card prop) or AADHAR_UIDAI.
const AADHAAR_DOC_TYPES = ['AADHAR', 'AADHAAR', 'AADHAR_UIDAI'];
const isAadhaar = (documentType?: string | null) =>
    !!documentType &&
    AADHAAR_DOC_TYPES.includes(documentType.trim().toUpperCase());

// The reveal trigger. Always rendered for an Indian org with a document, so an operator can see
// the control exists and why it is unavailable, rather than wondering whether the page is broken.
// Hidden entirely for non-India orgs: Aadhaar/PAN masking is India-only, so there is nothing to
// reveal and a disabled control there would be noise.
export const RevealButton: React.FC<BaseProps> = ({
    documentId,
    documentType,
    revealable,
    action,
    documentKeys,
    subject,
}) => {
    const classes = useStyles();
    const ctx = useKycReveal();
    const [modalOpen, setModalOpen] = useState(false);

    // The countdown tracks the first side; every side of one document is revealed in the same
    // call and shares a grant window, so one timer is the truth for all of them.
    const key = revealKeyOf(documentId, action, documentKeys?.[0] ?? null);
    const active = ctx?.revealed?.[key];
    const secondsLeft = useCountdown(active?.expiresAt);
    const isPending = ctx?.pendingKey === key;

    if (!ctx || !documentId) return null;
    // Aadhaar is never revealed, number or image: the blurred copy is all the ERP ever shows.
    if (isAadhaar(documentType)) return null;
    // null/undefined means the PII post-pass never ran for this rider's city, so the feature is
    // off there and there is nothing to reveal — render no control at all. An explicit false is
    // different: the pass DID run and refused this actor, which is worth showing as disabled so
    // the operator can tell "not permitted" apart from "does not exist here".
    //
    // Deliberately not keyed on the org's country any more: a Middle East city with the flag on
    // masks and encrypts numbers like any other, and must offer the same reveal.
    if (revealable === null || revealable === undefined) return null;
    if (!revealable) {
        return (
            <Tooltip title="You do not have permission to reveal this document, or its status does not allow it">
                <Typography className={classes.disabled}>
                    <VisibilityOutlinedIcon style={{ fontSize: 14 }} />
                    Reveal
                </Typography>
            </Tooltip>
        );
    }
    if (active && secondsLeft > 0) {
        return (
            <Typography className={classes.countdown}>
                hidden in {secondsLeft}s
            </Typography>
        );
    }

    return (
        <>
            <Tooltip title="Recorded against your name">
                <Typography
                    className={classes.link}
                    onClick={() => !isPending && setModalOpen(true)}
                >
                    {isPending ? (
                        <CircularProgress size={12} />
                    ) : (
                        <VisibilityOutlinedIcon style={{ fontSize: 14 }} />
                    )}
                    Reveal
                </Typography>
            </Tooltip>
            <KycRevealReasonModal
                open={modalOpen}
                subject={subject}
                loading={isPending}
                onClose={() => setModalOpen(false)}
                onConfirm={(reasonCode, reasonText) => {
                    setModalOpen(false);
                    ctx.requestReveal({
                        documentId,
                        documentType: documentType || '',
                        action,
                        documentKeys,
                        reasonCode,
                        reasonText,
                    });
                }}
            />
        </>
    );
};

/**
 * Masked value plus its reveal control. Shows the revealed value for as long as the grant lasts,
 * then falls back to the mask on its own.
 */
export const RevealableNumber: React.FC<
    Omit<BaseProps, 'action'> & {
        maskedValue?: string | null;
        className?: string;
    }
> = ({ maskedValue, className, ...rest }) => {
    const classes = useStyles();
    const ctx = useKycReveal();
    const key = revealKeyOf(rest.documentId, 'UNMASK_NUMBER');
    const active = ctx?.revealed?.[key];
    const secondsLeft = useCountdown(active?.expiresAt);
    const showRaw = !!active && secondsLeft > 0 && !!active.documentNo;

    return (
        <Box className={classes.row}>
            <Typography className={className}>
                {showRaw ? active?.documentNo : maskedValue || '-'}
            </Typography>
            <RevealButton {...rest} action="UNMASK_NUMBER" />
        </Box>
    );
};

/** Revealed doc number while a grant is live, else null so the caller shows its mask. */
export const useRevealedNumber = (
    documentId: number | string | undefined | null,
) => {
    const ctx = useKycReveal();
    const key = revealKeyOf(documentId, 'UNMASK_NUMBER');
    const active = ctx?.revealed?.[key];
    const secondsLeft = useCountdown(active?.expiresAt);
    if (active && secondsLeft > 0 && active.documentNo)
        return active.documentNo;
    return null;
};

/**
 * Resolves which image URL to show: the raw one while a grant is live, otherwise the blurred
 * one the API served. Returns null when the document has no viewable image at all, which the
 * caller must render as "processing" rather than as "not uploaded".
 */
export const useRevealedImage = (
    documentId: number | string | undefined | null,
    blurredUrl?: string | null,
    documentKey?: string | null,
) => {
    const ctx = useKycReveal();
    const key = revealKeyOf(documentId, 'UNBLUR_IMAGE', documentKey ?? null);
    const active = ctx?.revealed?.[key];
    const secondsLeft = useCountdown(active?.expiresAt);
    if (active && secondsLeft > 0 && active.rawImageUrl)
        return active.rawImageUrl;
    return blurredUrl ?? null;
};

// Flags that the image on screen is the ORIGINAL, because no blurred copy can exist for it -
// an unsupported document type, or ocr-svc could not locate the number. Driven by the server's
// imageUnmasked flag, which is never set while a mask is merely pending.
export const UnmaskedBadge: React.FC<{ visible?: boolean | null }> = ({
    visible,
}) => {
    const classes = useStyles();
    // No country check: imageUnmasked is only ever set by the PII pass, so it is already false
    // wherever the feature is off.
    if (!visible) return null;
    return (
        <Tooltip title="No blurred copy exists for this document. Viewing is recorded in the audit log.">
            <span className={classes.unmaskedBadge}>&#9888; Unmasked</span>
        </Tooltip>
    );
};

/** Shown in place of an image that has no blurred variant yet. Never falls back to the raw one. */
export const ImageProcessingNotice: React.FC<{ label?: string }> = ({
    label,
}) => {
    const classes = useStyles();
    return (
        <Typography className={classes.unavailable}>
            {label ||
                'Image is being processed and is not available yet. Refresh after a few seconds.'}
        </Typography>
    );
};
